import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import {
  Container,
  Paper,
  Title,
  Text,
  Stack,
  Group,
  Badge,
  Button,
  TextInput,
  ActionIcon,
  Textarea,
  Modal,
  SegmentedControl,
  Card,
  ThemeIcon,
  Tooltip,
  CopyButton,
  Loader,
  Center,
  Alert,
  Image,
  Divider,
  SimpleGrid,
  Box,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCheck,
  IconMessageCircle,
  IconArchive,
  IconLink,
  IconCopy,
  IconRefresh,
  IconAlertTriangle,
  IconPhone,
  IconMail,
  IconArrowLeft,
  IconExternalLink,
  IconUserPlus,
  IconNote,
} from "@tabler/icons-react";
import {
  useInboxLink,
  useCreateInboxLink,
  useUpdateInboxLink,
  useInboxSubmissions,
  useAcknowledgeSubmission,
  useReplyToSubmission,
  useArchiveSubmission,
  useCreateCustomization,
  type InboxSubmission,
} from "@/hooks/useInbox";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:8787";
const APP_URL = typeof window !== "undefined" ? window.location.origin : "";

export const Route = createFileRoute("/projects/$projectId/inbox")({
  component: ProjectInboxPage,
});

/**
 * Helper component to display photos from inbox submissions
 * Handles both single photos (string) and multiple photos (JSON array)
 */
function PhotoDisplay({
  photoUrl,
  inboxToken,
}: {
  photoUrl: string;
  inboxToken: string;
}) {
  // Parse photoUrl - could be single string or JSON array
  const parsePhotoUrls = (url: string): string[] => {
    // Check if it's a JSON array
    if (url.startsWith("[")) {
      try {
        const parsed = JSON.parse(url);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch {
        // Not valid JSON, treat as single URL
      }
    }
    // Single photo URL
    return [url];
  };

  // Extract filename from full R2 key (e.g., "inbox/{projectId}/{uuid}.jpg" -> "{uuid}.jpg")
  const getPhotoKey = (fullPath: string): string => {
    const parts = fullPath.split("/");
    return parts[parts.length - 1] || fullPath; // Get just the filename, fallback to full path
  };

  // Build public URL for photo (uses inbox token for access)
  const getPhotoUrl = (photoPath: string): string => {
    const photoKey = getPhotoKey(photoPath);
    return `${API_URL}/api/inbox/${inboxToken}/photo/${photoKey}`;
  };

  const photos = parsePhotoUrls(photoUrl);
  const firstPhoto = photos[0];

  if (photos.length === 1 && firstPhoto) {
    // Single photo - display larger
    return (
      <Image
        src={getPhotoUrl(firstPhoto)}
        alt="Submission photo"
        radius="md"
        mah={200}
        fit="contain"
        fallbackSrc="https://placehold.co/400x200?text=Photo"
      />
    );
  }

  // Multiple photos - display in a grid
  return (
    <SimpleGrid cols={photos.length <= 3 ? photos.length : 3} spacing="xs">
      {photos.map((photo, index) => (
        <Box key={index}>
          <Image
            src={getPhotoUrl(photo)}
            alt={`Submission photo ${index + 1}`}
            radius="md"
            h={120}
            fit="cover"
            fallbackSrc="https://placehold.co/200x120?text=Photo"
          />
        </Box>
      ))}
    </SimpleGrid>
  );
}

function ProjectInboxPage() {
  const { projectId } = Route.useParams();
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedSubmission, setSelectedSubmission] = useState<InboxSubmission | null>(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [replyModalOpened, { open: openReplyModal, close: closeReplyModal }] = useDisclosure(false);

  // Customization modal state
  const [customLinkModalOpened, { open: openCustomLinkModal, close: closeCustomLinkModal }] = useDisclosure(false);
  const [customPrefillName, setCustomPrefillName] = useState("");
  const [customGcNote, setCustomGcNote] = useState("");
  const [generatedCustomUrl, setGeneratedCustomUrl] = useState("");

  // Inbox link
  const { data: linkData, isLoading: loadingLink } = useInboxLink(projectId);
  const createLink = useCreateInboxLink(projectId);
  const updateLink = useUpdateInboxLink(projectId);

  // Submissions
  const { data: submissionsData, isLoading: loadingSubmissions, refetch } = useInboxSubmissions(
    projectId,
    statusFilter === "all" ? undefined : statusFilter
  );

  // Actions
  const acknowledgeSubmission = useAcknowledgeSubmission(projectId);
  const replyToSubmission = useReplyToSubmission(projectId);
  const archiveSubmission = useArchiveSubmission(projectId);
  const createCustomization = useCreateCustomization(projectId);

  // Get submit URL
  const getSubmitUrl = () => {
    if (!linkData?.link) return "";
    return `${APP_URL}/submit/${linkData.link.token}`;
  };

  // Handle reply submit
  const handleReply = async () => {
    if (!selectedSubmission || !replyMessage.trim()) return;
    await replyToSubmission.mutateAsync({
      submissionId: selectedSubmission.id,
      message: replyMessage,
    });
    setReplyMessage("");
    setSelectedSubmission(null);
    closeReplyModal();
  };

  // Handle customization creation
  const handleCreateCustomLink = async () => {
    if (!customPrefillName.trim() && !customGcNote.trim()) return;
    const result = await createCustomization.mutateAsync({
      prefillName: customPrefillName.trim() || undefined,
      gcNote: customGcNote.trim() || undefined,
    });

    // Construct URL on frontend (API returns its own domain which is wrong)
    const baseUrl = getSubmitUrl();
    const params = new URLSearchParams();
    if (result.customization.prefillName) {
      params.set("name", result.customization.prefillName);
    }
    params.set("ref", result.customization.shortCode);
    setGeneratedCustomUrl(`${baseUrl}?${params.toString()}`);
  };

  // Reset customization modal
  const resetCustomLinkModal = () => {
    setCustomPrefillName("");
    setCustomGcNote("");
    setGeneratedCustomUrl("");
    closeCustomLinkModal();
  };

  // Category badge color
  const getCategoryColor = (category: string) => {
    switch (category) {
      case "checkpoint":
        return "green";
      case "issue":
        return "red";
      case "delivery":
        return "blue";
      case "receipt":
        return "orange";
      default:
        return "gray";
    }
  };

  // Status badge
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <Badge color="blue">New</Badge>;
      case "acknowledged":
        return <Badge color="green">Acknowledged</Badge>;
      case "replied":
        return <Badge color="teal">Replied</Badge>;
      case "linked":
        return <Badge color="violet">Linked</Badge>;
      case "archived":
        return <Badge color="gray">Archived</Badge>;
      default:
        return null;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <Container size="lg" py="md">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group gap="xs">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              size="sm"
              onClick={() => navigate({ to: "/projects/$projectId", params: { projectId } })}
            >
              Back
            </Button>
            <Title order={2}>
              <Group gap="xs">
                <IconMessageCircle size={24} />
                Messages
              </Group>
            </Title>
          </Group>
          <Button
            variant="light"
            leftSection={<IconRefresh size={16} />}
            onClick={() => refetch()}
            loading={loadingSubmissions}
          >
            Refresh
          </Button>
        </Group>

        {/* Inbox Link Card */}
        <Paper p="lg" shadow="sm" radius="md" withBorder>
          <Stack gap="md">
            <Group justify="space-between">
              <div>
                <Text fw={600}>Public Submission Link</Text>
                <Text size="sm" c="dimmed">
                  Share this link with anyone to receive submissions
                </Text>
              </div>
              {linkData?.link && (
                <Badge color={linkData.link.isActive ? "green" : "red"} size="lg">
                  {linkData.link.isActive ? "Active" : "Disabled"}
                </Badge>
              )}
            </Group>

            {loadingLink ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : linkData?.link ? (
              <Stack gap="sm">
                <Group gap="xs">
                  <TextInput
                    value={getSubmitUrl()}
                    readOnly
                    style={{ flex: 1 }}
                    rightSection={
                      <CopyButton value={getSubmitUrl()}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? "Copied!" : "Copy"}>
                            <ActionIcon
                              color={copied ? "teal" : "gray"}
                              onClick={copy}
                              variant="subtle"
                            >
                              <IconCopy size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    }
                  />
                  <Tooltip label="Open in new tab">
                    <ActionIcon
                      component="a"
                      href={getSubmitUrl()}
                      target="_blank"
                      variant="light"
                    >
                      <IconExternalLink size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
                <Group gap="xs">
                  <Button
                    size="xs"
                    variant="light"
                    color="blue"
                    leftSection={<IconUserPlus size={14} />}
                    onClick={openCustomLinkModal}
                  >
                    Get Customized Link
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color={linkData.link.isActive ? "red" : "green"}
                    onClick={() =>
                      updateLink.mutate({ isActive: !linkData.link!.isActive })
                    }
                    loading={updateLink.isPending}
                  >
                    {linkData.link.isActive ? "Disable Link" : "Enable Link"}
                  </Button>
                  <Button
                    size="xs"
                    variant="light"
                    color="gray"
                    onClick={() => updateLink.mutate({ regenerateToken: true })}
                    loading={updateLink.isPending}
                  >
                    Regenerate Link
                  </Button>
                </Group>
              </Stack>
            ) : (
              <Button
                onClick={() => createLink.mutate()}
                loading={createLink.isPending}
                leftSection={<IconLink size={16} />}
              >
                Create Submission Link
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Filter */}
        <SegmentedControl
          value={statusFilter}
          onChange={setStatusFilter}
          data={[
            { label: `All (${submissionsData?.total || 0})`, value: "all" },
            { label: `New (${submissionsData?.newCount || 0})`, value: "new" },
            { label: "Acknowledged", value: "acknowledged" },
            { label: "Replied", value: "replied" },
            { label: "Archived", value: "archived" },
          ]}
        />

        {/* Submissions List */}
        {loadingSubmissions ? (
          <Center py="xl">
            <Loader size="lg" />
          </Center>
        ) : submissionsData?.submissions.length === 0 ? (
          <Paper p="xl" shadow="sm" radius="md" withBorder>
            <Stack align="center" gap="md">
              <ThemeIcon size={60} radius="xl" color="gray" variant="light">
                <IconMessageCircle size={30} />
              </ThemeIcon>
              <Title order={3}>No Messages Yet</Title>
              <Text c="dimmed" ta="center">
                Share your link with vendors, contractors, or clients to
                receive project updates.
              </Text>
            </Stack>
          </Paper>
        ) : (
          <Stack gap="md">
            {submissionsData?.submissions.map((submission) => (
              <Card key={submission.id} shadow="sm" radius="md" withBorder p="lg">
                <Stack gap="md">
                  {/* Header */}
                  <Group justify="space-between">
                    <Group gap="xs">
                      <Text fw={600}>{submission.submitterName}</Text>
                      <Badge color={getCategoryColor(submission.category)} size="sm">
                        {submission.category}
                      </Badge>
                      {getStatusBadge(submission.status)}
                    </Group>
                    <Text size="sm" c="dimmed">
                      {submission.referenceNumber}
                    </Text>
                  </Group>

                  {/* Contact info */}
                  {(submission.submitterPhone || submission.submitterEmail) && (
                    <Group gap="md">
                      {submission.submitterPhone && (
                        <Group gap="xs">
                          <IconPhone size={14} color="gray" />
                          <Text size="sm" c="dimmed">
                            {submission.submitterPhone}
                          </Text>
                        </Group>
                      )}
                      {submission.submitterEmail && (
                        <Group gap="xs">
                          <IconMail size={14} color="gray" />
                          <Text size="sm" c="dimmed">
                            {submission.submitterEmail}
                          </Text>
                        </Group>
                      )}
                    </Group>
                  )}

                  {/* GC Note from customization */}
                  {submission.customization?.gcNote && (
                    <Paper p="xs" bg="blue.0" radius="sm">
                      <Group gap="xs">
                        <IconNote size={14} color="var(--mantine-color-blue-6)" />
                        <Text size="sm" c="blue.7" fw={500}>
                          {submission.customization.gcNote}
                        </Text>
                      </Group>
                    </Paper>
                  )}

                  {/* Message */}
                  <Text>{submission.message}</Text>

                  {/* Photos - handles both single and multiple */}
                  {submission.photoUrl && linkData?.link?.token && (
                    <PhotoDisplay
                      photoUrl={submission.photoUrl}
                      inboxToken={linkData.link.token}
                    />
                  )}

                  {/* Reply if exists */}
                  {submission.replyMessage && (
                    <>
                      <Divider />
                      <Paper p="sm" bg="gray.0" radius="sm">
                        <Text size="sm" fw={500} c="dimmed" mb="xs">
                          Your Reply:
                        </Text>
                        <Text size="sm">{submission.replyMessage}</Text>
                      </Paper>
                    </>
                  )}

                  {/* Footer */}
                  <Group justify="space-between">
                    <Text size="xs" c="dimmed">
                      {formatDate(submission.createdAt)}
                    </Text>

                    {/* Actions */}
                    {submission.status !== "archived" && (
                      <Group gap="xs">
                        {submission.status === "new" && (
                          <Button
                            size="xs"
                            variant="light"
                            color="green"
                            leftSection={<IconCheck size={14} />}
                            onClick={() => acknowledgeSubmission.mutate(submission.id)}
                            loading={acknowledgeSubmission.isPending}
                          >
                            Acknowledge
                          </Button>
                        )}
                        {!submission.replyMessage && (
                          <Button
                            size="xs"
                            variant="light"
                            color="blue"
                            leftSection={<IconMessageCircle size={14} />}
                            onClick={() => {
                              setSelectedSubmission(submission);
                              openReplyModal();
                            }}
                          >
                            Reply
                          </Button>
                        )}
                        <Button
                          size="xs"
                          variant="light"
                          color="gray"
                          leftSection={<IconArchive size={14} />}
                          onClick={() => archiveSubmission.mutate(submission.id)}
                          loading={archiveSubmission.isPending}
                        >
                          Archive
                        </Button>
                      </Group>
                    )}
                  </Group>
                </Stack>
              </Card>
            ))}
          </Stack>
        )}
      </Stack>

      {/* Reply Modal */}
      <Modal
        opened={replyModalOpened}
        onClose={() => {
          closeReplyModal();
          setSelectedSubmission(null);
          setReplyMessage("");
        }}
        title="Reply to Submission"
        size="md"
      >
        {selectedSubmission && (
          <Stack gap="md">
            <Paper p="sm" bg="gray.0" radius="sm">
              <Text size="sm" c="dimmed" mb="xs">
                Original Message from {selectedSubmission.submitterName}:
              </Text>
              <Text size="sm">{selectedSubmission.message}</Text>
            </Paper>

            <Textarea
              label="Your Reply"
              placeholder="Type your reply..."
              minRows={3}
              value={replyMessage}
              onChange={(e) => setReplyMessage(e.target.value)}
            />

            {selectedSubmission.submitterEmail || selectedSubmission.submitterPhone ? (
              <Alert color="blue" icon={<IconMail size={16} />}>
                {selectedSubmission.submitterEmail
                  ? `Reply will be sent to ${selectedSubmission.submitterEmail}`
                  : `Submitter can be reached at ${selectedSubmission.submitterPhone}`}
              </Alert>
            ) : (
              <Alert color="yellow" icon={<IconAlertTriangle size={16} />}>
                Submitter did not provide contact info. Reply will be saved but
                cannot be sent.
              </Alert>
            )}

            <Group justify="flex-end">
              <Button variant="light" onClick={closeReplyModal}>
                Cancel
              </Button>
              <Button
                onClick={handleReply}
                loading={replyToSubmission.isPending}
                disabled={!replyMessage.trim()}
              >
                Send Reply
              </Button>
            </Group>
          </Stack>
        )}
      </Modal>

      {/* Customized Link Modal */}
      <Modal
        opened={customLinkModalOpened}
        onClose={resetCustomLinkModal}
        title="Get Customized Link"
        size="md"
      >
        <Stack gap="md">
          {!generatedCustomUrl ? (
            <>
              <Text size="sm" c="dimmed">
                Create a personalized submission link with pre-filled information and a note for yourself.
              </Text>

              <TextInput
                label="Pre-fill Name"
                description="This name will be pre-filled in the form when the recipient opens the link"
                placeholder="e.g., Mike from ABC Plumbing"
                value={customPrefillName}
                onChange={(e) => setCustomPrefillName(e.target.value)}
              />

              <Textarea
                label="Note for Yourself"
                description="This note will appear on submissions from this link (only visible to you)"
                placeholder="e.g., Tile deliveries - Phase 2"
                minRows={2}
                value={customGcNote}
                onChange={(e) => setCustomGcNote(e.target.value)}
              />

              <Group justify="flex-end">
                <Button variant="light" onClick={resetCustomLinkModal}>
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateCustomLink}
                  loading={createCustomization.isPending}
                  disabled={!customPrefillName.trim() && !customGcNote.trim()}
                  leftSection={<IconLink size={16} />}
                >
                  Generate Link
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Alert color="green" icon={<IconCheck size={16} />}>
                Your customized link has been created!
              </Alert>

              <Stack gap="xs">
                <Text size="sm" fw={500}>Customized Link:</Text>
                <Group gap="xs">
                  <TextInput
                    value={generatedCustomUrl}
                    readOnly
                    style={{ flex: 1 }}
                    rightSection={
                      <CopyButton value={generatedCustomUrl}>
                        {({ copied, copy }) => (
                          <Tooltip label={copied ? "Copied!" : "Copy"}>
                            <ActionIcon
                              color={copied ? "teal" : "gray"}
                              onClick={copy}
                              variant="subtle"
                            >
                              <IconCopy size={16} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </CopyButton>
                    }
                  />
                  <Tooltip label="Open in new tab">
                    <ActionIcon
                      component="a"
                      href={generatedCustomUrl}
                      target="_blank"
                      variant="light"
                    >
                      <IconExternalLink size={16} />
                    </ActionIcon>
                  </Tooltip>
                </Group>
              </Stack>

              {customPrefillName && (
                <Text size="sm" c="dimmed">
                  <strong>Pre-filled name:</strong> {customPrefillName}
                </Text>
              )}
              {customGcNote && (
                <Text size="sm" c="dimmed">
                  <strong>Your note:</strong> {customGcNote}
                </Text>
              )}

              <Group justify="flex-end">
                <Button variant="light" onClick={resetCustomLinkModal}>
                  Done
                </Button>
                <Button
                  variant="light"
                  onClick={() => setGeneratedCustomUrl("")}
                >
                  Create Another
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Container>
  );
}
